import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  Bars3Icon,
  HomeIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useGuardianAuthStore } from '../../stores/guardianAuthStore';
import { H3, Badge } from '../ui';
import { GuardianNotificationBell } from '../notifications';

interface GuardianNavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const guardianNavigation: GuardianNavItem[] = [
  { name: 'Dashboard', href: '/guardian/dashboard', icon: HomeIcon },
  { name: 'My Children', href: '/guardian/children', icon: UserGroupIcon },
  { name: 'Observations', href: '/guardian/observations', icon: ClipboardDocumentListIcon },
  { name: 'Assessments', href: '/guardian/assessments', icon: DocumentTextIcon },
  { name: 'Profile', href: '/guardian/profile', icon: UserCircleIcon },
];

const GuardianLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { guardian, logout } = useGuardianAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/guardian/login');
  };

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 40,
      },
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 40,
      },
    },
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-10 h-10 bg-primary-500 rounded-xl mr-3">
            <UserGroupIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <H3 className="text-gray-900 dark:text-gray-100">Sprout</H3>
            <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">Parent Portal</span>
          </div>
        </div>

        {/* Close button - mobile only */}
        <button
          onClick={closeSidebar}
          className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {guardianNavigation.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/guardian/dashboard' &&
              location.pathname.startsWith(item.href));

          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={closeSidebar}
              className={() =>
                `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-r-2 border-primary-500'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                }`
              }
            >
              <item.icon
                className={`w-5 h-5 mr-3 ${
                  isActive ? 'text-primary-500 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                }`}
              />
              {item.name}
            </NavLink>
          );
        })}
      </nav>

      {/* User info footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3 mb-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
                {guardian?.first_name?.[0]}
                {guardian?.last_name?.[0]}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {guardian?.first_name} {guardian?.last_name}
            </p>
            <Badge variant="primary" size="sm">
              Guardian
            </Badge>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-50 lg:hidden"
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Mobile sidebar with animation */}
      <motion.div
        variants={sidebarVariants}
        initial="closed"
        animate={sidebarOpen ? 'open' : 'closed'}
        className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-xl border-r border-gray-200 dark:border-gray-700 lg:hidden"
      >
        {sidebarContent}
      </motion.div>

      {/* Desktop sidebar - always visible */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 z-40 bg-white dark:bg-gray-800 shadow-xl border-r border-gray-200 dark:border-gray-700">
        {sidebarContent}
      </div>

      {/* Main content area */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-soft border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 lg:px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left side - Mobile menu button */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleSidebar}
                  className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <Bars3Icon className="w-6 h-6" />
                </button>

                {/* Logo - visible on mobile */}
                <div className="flex items-center lg:hidden">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-500 rounded-lg mr-2">
                    <UserGroupIcon className="w-5 h-5 text-white" />
                  </div>
                  <H3 className="text-gray-900 dark:text-gray-100">Sprout</H3>
                </div>
              </div>

              {/* Right side - notifications + Guardian name */}
              <div className="flex items-center space-x-3">
                <GuardianNotificationBell />
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {guardian?.first_name} {guardian?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Parent Portal</p>
                </div>
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
                    {guardian?.first_name?.[0]}
                    {guardian?.last_name?.[0]}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="p-4 lg:p-6 mobile-safe-bottom">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 lg:hidden">
        <div className="flex justify-around">
          {guardianNavigation.slice(0, 5).map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== '/guardian/dashboard' &&
                location.pathname.startsWith(item.href));

            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center py-2 px-3 min-w-0 flex-1 ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <item.icon
                  className={`w-6 h-6 ${
                    isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                  }`}
                />
                <span
                  className={`text-xs mt-1 truncate ${
                    isActive ? 'font-medium' : 'font-normal'
                  }`}
                >
                  {item.name}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default GuardianLayout;
